import { supabase } from "@/integrations/supabase/client";
import {
  getUserSubscriptionTier,
  BOOKMARK_LIMITS,
} from "@/utils/subscriptionUtils";
import type { User } from "@supabase/supabase-js";

export interface Region {
  longitude: any;
  latitude: any;
  id: string;
  name: string;
  coordinates: [number, number];
  country: string;
  continent: string;
}

export interface EnvironmentalData {
  regionId: string;
  updatedAt?: string; // Add updated_at field
  sources?: string[];
  deforestation: {
    ratePerYear: number; // percentage
    totalLost: number; // square kilometers
    primaryCauses: string[];
    yearlyData: { year: number; value: number }[];
  };
  landLoss: {
    ratePerYear: number; // percentage
    totalLost: number; // square kilometers
    primaryCauses: string[];
    yearlyData: { year: number; value: number }[];
  };
  desertification: {
    ratePerYear: number; // percentage
    affectedArea: number; // square kilometers
    primaryCauses: string[];
    riskLevel: "Low" | "Medium" | "High" | "Critical";
    yearlyData: { year: number; value: number }[];
  };
  endangeredSpecies: {
    count: number;
    criticallyEndangered: number;
    endangered: number;
    vulnerable: number;
    topThreatenedSpecies: {
      name: string;
      scientificName: string;
      status: "Critically Endangered" | "Endangered" | "Vulnerable";
      population: number | "Unknown";
      trend: "Decreasing" | "Stable" | "Increasing";
    }[];
  };
}

export interface SavedRegion {
  id: string;
  user_id: string;
  region_id: string;
  created_at: string;
}

export interface SavedRegionResponse {
  id: string;
  user_id: string;
  region_id: string;
  created_at: string;
  region: Region | null;
}

export interface SavedIceberg {
  id: string;
  user_id: string;
  iceberg_id: string;
  created_at: string;
}

export interface ContinentData {
  deforestation: {
    ratePerYear: number;
    totalLost: number;
    yearlyData: { year: number; value: number }[];
    primaryCauses: string[];
  };
  landLoss: {
    ratePerYear: number;
    totalLost: number;
    yearlyData: { year: number; value: number }[];
    primaryCauses: string[];
  };
  desertification: {
    ratePerYear: number;
    affectedArea: number;
    yearlyData: { year: number; value: number }[];
    primaryCauses: string[];
    riskLevel: "Low" | "Medium" | "High" | "Critical";
  };
  endangeredSpecies: {
    count: number;
    criticallyEndangered: number;
    endangered: number;
    vulnerable: number;
    topThreatenedSpecies: {
      name: string;
      scientificName: string;
      status: "Critically Endangered" | "Endangered" | "Vulnerable";
      population: number | "Unknown";
      trend: "Decreasing" | "Stable" | "Increasing";
    }[];
  };
}

export const fetchAllRegions = async (): Promise<Region[]> => {
  try {
    console.log("Fetching all regions from Supabase...");
    const { data, error } = await supabase.from("regions").select("*");

    if (error) {
      console.error("Error fetching regions:", error);
      throw new Error(`Failed to fetch regions: ${error.message}`);
    }

    if (!data || data.length === 0) {
      console.warn("No regions found in the database");
      return [];
    }

    console.log(`Successfully fetched ${data.length} regions from Supabase`);

    // Transform coordinates from numeric array to tuple
    return data.map((region) => ({
      ...region,
      coordinates: region.coordinates as unknown as [number, number],
    }));
  } catch (error) {
    console.error("Unexpected error in fetchAllRegions:", error);
    throw error;
  }
};

export const fetchRegionById = async (id: string): Promise<Region | null> => {
  try {
    console.log("Fetching region with ID:", id);

    const { data, error } = await supabase
      .from("regions")
      .select("*")
      .eq("id", id)
      .single();

    if (error) {
      if (error.code === "PGRST116") {
        // No rows returned
        console.log("No region found with ID:", id);
        return null;
      }
      console.error(`Error fetching region ${id}:`, error);
      throw new Error(`Failed to fetch region: ${error.message}`);
    }

    if (!data) {
      console.warn(`No data returned for region ID: ${id}`);
      return null;
    }

    console.log("Successfully fetched region data:", data);

    return {
      ...data,
      coordinates: data.coordinates as unknown as [number, number],
    };
  } catch (error) {
    console.error("Unexpected error in fetchRegionById:", error);
    throw error;
  }
};

export const fetchEnvironmentalData = async (
  regionId: string,
): Promise<EnvironmentalData | null> => {
  try {
    console.log("Fetching environmental data for region ID:", regionId);

    const { data, error } = await supabase
      .from("environmental_data")
      .select("*")
      .eq("region_id", regionId)
      .single();

    if (error) {
      if (error.code === "PGRST116") {
        // No rows returned
        console.log("No environmental data found for region ID:", regionId);
        return null;
      }
      console.error(
        `Error fetching environmental data for region ${regionId}:`,
        error,
      );
      throw new Error(`Failed to fetch environmental data: ${error.message}`);
    }

    if (!data) {
      console.warn(`No data returned for region ID: ${regionId}`);
      return null;
    }

    console.log("Successfully fetched environmental data:", data);
    console.log("Raw environmental data from Supabase:", data);

    // Transform the data from database format to application format with proper type casting
    const transformedData = {
      regionId: (data as any).region_id,
      updatedAt: (data as any).updated_at,
      sources: (data as any).sources as string[] | undefined,
      deforestation: {
        ratePerYear: Number((data as any).deforestation_rate_per_year) || 0,
        totalLost: Number((data as any).deforestation_total_lost) || 0,
        primaryCauses: (data as any).deforestation_primary_causes,
        yearlyData: (data as any).deforestation_yearly_data as {
          year: number;
          value: number;
        }[],
      },
      landLoss: {
        ratePerYear: Number((data as any).land_loss_rate_per_year) || 0,
        totalLost: Number((data as any).land_loss_total_lost) || 0,
        primaryCauses: (data as any).land_loss_primary_causes,
        yearlyData: (data as any).land_loss_yearly_data as {
          year: number;
          value: number;
        }[],
      },
      desertification: {
        ratePerYear: Number((data as any).desertification_rate_per_year) || 0,
        affectedArea: Number((data as any).desertification_affected_area) || 0,
        primaryCauses: (data as any).desertification_primary_causes,
        riskLevel: (data as any).desertification_risk_level as
          | "Low"
          | "Medium"
          | "High"
          | "Critical",
        yearlyData: (data as any).desertification_yearly_data as {
          year: number;
          value: number;
        }[],
      },
      endangeredSpecies: {
        count: Number((data as any).endangered_species_count) || 0,
        criticallyEndangered:
          Number((data as any).endangered_species_critically_endangered) || 0,
        endangered: Number((data as any).endangered_species_endangered) || 0,
        vulnerable: Number((data as any).endangered_species_vulnerable) || 0,
        topThreatenedSpecies: (data as any)
          .endangered_species_top_threatened as {
          name: string;
          scientificName: string;
          status: "Critically Endangered" | "Endangered" | "Vulnerable";
          population: number | "Unknown";
          trend: "Decreasing" | "Stable" | "Increasing";
        }[],
      },
    };

    console.log("Transformed environmental data:", {
      landLoss: transformedData.landLoss,
      deforestation: transformedData.deforestation,
    });

    return transformedData;
  } catch (error) {
    console.error("Unexpected error in fetchEnvironmentalData:", error);
    throw error;
  }
};

/**
 * Check if a user can save more regions based on their subscription tier
 */
export const canSaveRegion = async (
  user: User | null,
): Promise<{
  allowed: boolean;
  remaining: number;
  message: string;
}> => {
  // If no user, deny access
  if (!user) {
    return {
      allowed: false,
      remaining: 0,
      message: "You must be logged in to save regions.",
    };
  }

  try {
    // Get user's subscription tier
    const tier = await getUserSubscriptionTier(user.id);
    const bookmarkLimit = BOOKMARK_LIMITS[tier];

    if (!bookmarkLimit) {
      return {
        allowed: false,
        remaining: 0,
        message: "Unable to determine bookmark limit for your plan.",
      };
    }

    // Count existing saved regions
    const { data, error, count } = await supabase
      .from("saved_regions")
      .select("id", { count: "exact" })
      .eq("user_id", user.id);

    if (error) {
      console.error("Error checking saved regions count:", error);
      // On error, allow saving to prevent blocking users
      return {
        allowed: true,
        remaining: bookmarkLimit,
        message: "Unable to check bookmark limit. Please try again later.",
      };
    }

    const savedCount = count || 0;
    const remaining = bookmarkLimit - savedCount;

    if (remaining <= 0) {
      const tierName = tier === "PAYG" ? "PAYG" : "Professional";
      const upgradeTier = "Professional";

      return {
        allowed: false,
        remaining: 0,
        message: `Fair-use limit of ${bookmarkLimit} saved regions reached.`,
      };
    }

    const tierName = tier === "PAYG" ? "PAYG" : "Professional";

    return {
      allowed: true,
      remaining: remaining,
      message: `You can save ${remaining} more ${remaining === 1 ? "region" : "regions"} on your ${tierName} plan.`,
    };
  } catch (error) {
    console.error("Error checking region save limits:", error);
    return {
      allowed: true,
      remaining: 0,
      message: "Unable to verify bookmark limits. Please try again later.",
    };
  }
};

export const saveRegionForUser = async (
  userId: string,
  regionId: string,
): Promise<SavedRegionResponse> => {
  // Enforce fair-use limit: maximum 10 saved regions per user
  try {
    const {
      data: countData,
      error: countError,
      count,
    } = await supabase
      .from("saved_regions")
      .select("id", { count: "exact" })
      .eq("user_id", userId);

    if (countError) {
      console.error(
        "Error checking saved regions count before insert:",
        countError,
      );
      // If we can't determine the count, allow the save to proceed to avoid blocking users.
    } else {
      const savedCount = count || 0;
      const FAIR_USE_LIMIT = 10;
      if (savedCount >= FAIR_USE_LIMIT) {
        throw new Error(
          `Fair-use limit of ${FAIR_USE_LIMIT} saved regions reached.`,
        );
      }
    }
  } catch (err) {
    // If an unexpected error occurred during the count check, log and rethrow so callers can inform the user.
    if (err instanceof Error) throw err;
    throw new Error("Unexpected error while checking saved regions limit.");
  }

  // Check if already saved to avoid insert conflicts (table may lack unique constraint)
  const { data: existing, error: existingError } = await supabase
    .from("saved_regions")
    .select("id, user_id, region_id, created_at")
    .match({ user_id: userId, region_id: regionId })
    .maybeSingle();

  if (existingError) throw existingError;
  if (existing) return { ...existing, region: null } as SavedRegionResponse;

  const { data, error } = await supabase
    .from("saved_regions")
    .insert([{ user_id: userId, region_id: regionId }])
    .select("id, user_id, region_id, created_at")
    .single();

  if (error) throw error;
  if (!data) throw new Error("Failed to retrieve saved region");

  return { ...data, region: null } as SavedRegionResponse;
};

export const unsaveRegionForUser = async (
  userId: string,
  regionId: string,
): Promise<void> => {
  const { error } = await supabase
    .from("saved_regions")
    .delete()
    .match({ user_id: userId, region_id: regionId });

  if (error) throw error;
};

export const getUserSavedRegions = async (
  userId: string,
): Promise<SavedRegionResponse[]> => {
  const { data, error } = await supabase
    .from("saved_regions")
    .select(
      `
      id,
      user_id,
      region_id,
      created_at,
      region:regions (
        id,
        name,
        coordinates,
        country,
        continent
      )
    `,
    )
    .eq("user_id", userId);

  if (error) throw error;
  if (!data) return [];

  return data as SavedRegionResponse[];
};

export const isRegionSavedByUser = async (
  userId: string,
  regionId: string,
): Promise<boolean> => {
  const { data, error } = await supabase
    .from("saved_regions")
    .select("id")
    .match({ user_id: userId, region_id: regionId })
    .maybeSingle();

  if (error) return false;
  return !!data;
};

// ---------------------------------------------------------------------------
// Iceberg bookmark functions (saved_icebergs table)
// ---------------------------------------------------------------------------

export const isIcebergSavedByUser = async (
  userId: string,
  icebergId: string,
): Promise<boolean> => {
  const { data, error } = await supabase
    .from("saved_icebergs")
    .select("id")
    .match({ user_id: userId, iceberg_id: icebergId })
    .maybeSingle();

  if (error) return false;
  return !!data;
};

export const saveIcebergForUser = async (
  userId: string,
  icebergId: string,
): Promise<SavedIceberg> => {
  // Return existing row instead of inserting a duplicate
  const { data: existing } = await supabase
    .from("saved_icebergs")
    .select("id, user_id, iceberg_id, created_at")
    .match({ user_id: userId, iceberg_id: icebergId })
    .maybeSingle();

  if (existing) return existing as SavedIceberg;

  const { data, error } = await supabase
    .from("saved_icebergs")
    .insert([{ user_id: userId, iceberg_id: icebergId }])
    .select("id, user_id, iceberg_id, created_at")
    .single();

  if (error) throw error;
  return data as SavedIceberg;
};

export const unsaveIcebergForUser = async (
  userId: string,
  icebergId: string,
): Promise<void> => {
  const { error } = await supabase
    .from("saved_icebergs")
    .delete()
    .match({ user_id: userId, iceberg_id: icebergId });

  if (error) throw error;
};

export const getUserSavedIcebergs = async (
  userId: string,
): Promise<SavedIceberg[]> => {
  const { data, error } = await supabase
    .from("saved_icebergs")
    .select("id, user_id, iceberg_id, created_at")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (data ?? []) as SavedIceberg[];
};

export const fetchContinentData = async (
  continent: string,
): Promise<ContinentData | null> => {
  try {
    console.log("Fetching environmental data for continent:", continent);

    // First, get all environmental data for regions in this continent
    const { data: regions, error: regionsError } = await supabase
      .from("regions")
      .select(
        `
        id,
        environmental_data!inner (
          deforestation_rate_per_year,
          deforestation_total_lost,
          deforestation_yearly_data,
          desertification_rate_per_year,
          desertification_affected_area,
          desertification_yearly_data,
          land_loss_rate_per_year,
          land_loss_total_lost,
          land_loss_yearly_data,
          endangered_species_count,
          endangered_species_critically_endangered,
          endangered_species_endangered,
          endangered_species_vulnerable,
          endangered_species_top_threatened
        )
      `,
      )
      .eq("continent", continent);

    if (regionsError) {
      console.error("Error fetching continent data:", regionsError);
      throw new Error(
        `Failed to fetch continent data: ${regionsError.message}`,
      );
    }

    console.log("Raw regions data from Supabase:", regions);
    console.log("Number of regions found:", regions?.length || 0);

    if (!regions || regions.length === 0) {
      console.log("No data found for continent:", continent);
      return null;
    }

    // Initialize aggregated data structure
    const aggregatedData: ContinentData = {
      deforestation: {
        ratePerYear: 0,
        totalLost: 0,
        yearlyData: [],
        primaryCauses: [],
      },
      landLoss: {
        ratePerYear: 0,
        totalLost: 0,
        yearlyData: [],
        primaryCauses: [],
      },
      desertification: {
        ratePerYear: 0,
        affectedArea: 0,
        yearlyData: [],
        primaryCauses: [],
        riskLevel: "Low",
      },
      endangeredSpecies: {
        count: 0,
        criticallyEndangered: 0,
        endangered: 0,
        vulnerable: 0,
        topThreatenedSpecies: [],
      },
    };

    let validRegionsCount = 0;

    regions.forEach((region) => {
      console.log("Processing region:", region);
      // Check if environmental_data exists (it's an object, not an array)
      if (
        region.environmental_data &&
        typeof region.environmental_data === "object"
      ) {
        console.log(
          "Region has environmental data:",
          region.environmental_data,
        );
        validRegionsCount++;
        const envData = region.environmental_data; // Direct access to the object
        console.log("Environmental data for aggregation:", envData);

        // Aggregate deforestation data
        aggregatedData.deforestation.ratePerYear +=
          envData.deforestation_rate_per_year || 0;
        aggregatedData.deforestation.totalLost +=
          envData.deforestation_total_lost || 0;

        // Aggregate land loss data
        aggregatedData.landLoss.ratePerYear +=
          envData.land_loss_rate_per_year || 0;
        aggregatedData.landLoss.totalLost += envData.land_loss_total_lost || 0;

        // Aggregate desertification data
        aggregatedData.desertification.ratePerYear +=
          envData.desertification_rate_per_year || 0;
        aggregatedData.desertification.affectedArea +=
          envData.desertification_affected_area || 0;

        // Aggregate endangered species data
        aggregatedData.endangeredSpecies.count +=
          envData.endangered_species_count || 0;
        aggregatedData.endangeredSpecies.criticallyEndangered +=
          envData.endangered_species_critically_endangered || 0;
        aggregatedData.endangeredSpecies.endangered +=
          envData.endangered_species_endangered || 0;
        aggregatedData.endangeredSpecies.vulnerable +=
          envData.endangered_species_vulnerable || 0;

        // Add threatened species
        const species =
          (envData.endangered_species_top_threatened as any[]) || [];
        aggregatedData.endangeredSpecies.topThreatenedSpecies.push(...species);
      }
    });

    // Calculate averages for rate-based metrics
    if (validRegionsCount > 0) {
      aggregatedData.deforestation.ratePerYear /= validRegionsCount;
      aggregatedData.landLoss.ratePerYear /= validRegionsCount;
      aggregatedData.desertification.ratePerYear /= validRegionsCount;
    }

    // Deduplicate and sort threatened species by population
    aggregatedData.endangeredSpecies.topThreatenedSpecies = Array.from(
      new Map(
        aggregatedData.endangeredSpecies.topThreatenedSpecies.map((s) => [
          s.name,
          s,
        ]),
      ).values(),
    )
      .sort((a, b) => {
        const popA = typeof a.population === "number" ? a.population : 0;
        const popB = typeof b.population === "number" ? b.population : 0;
        return popB - popA;
      })
      .slice(0, 10); // Keep only top 10

    console.log("Successfully aggregated continent data:", aggregatedData);
    return aggregatedData;
  } catch (error) {
    console.error("Error in fetchContinentData:", error);
    throw error;
  }
};
